import React, { useEffect } from 'react'
import { useField } from 'formik'
import './ui.scss'

// Page-top hero shared by the inner pages: animated orbs, grid and staggered copy.
export const PageHero = ({ eyebrow, title, subtitle, children, back }) => (
  <header className="page-hero">
    <div className="page-hero__orb orb-a" aria-hidden="true" />
    <div className="page-hero__orb orb-b" aria-hidden="true" />
    <div className="page-hero__grid" aria-hidden="true" />
    <div className="ui-container page-hero__inner">
      {back}
      {eyebrow && <p className="ui-eyebrow">{eyebrow}</p>}
      <h1>{title}</h1>
      {subtitle && <p className="page-hero__sub">{subtitle}</p>}
      {children}
    </div>
  </header>
)

// Formik input with a floating label, live validation state and optional hint / counter.
export const FormField = ({ label, hint, maxLength, as = 'input', onChange, className = '', ...props }) => {
  const [field, meta, helpers] = useField(props.name)
  const invalid = meta.touched && meta.error
  const valid = meta.touched && !meta.error && field.value
  const Tag = as

  const handleChange = (event) => {
    if (onChange) onChange(event)
    else field.onChange(event)
    // Validate while typing once the field has been visited.
    if (meta.touched) helpers.setTouched(true, true)
  }

  return (
    <div className={`ui-field ${invalid ? 'is-invalid' : ''} ${valid ? 'is-valid' : ''} ${className}`}>
      <Tag
        {...field}
        {...props}
        id={props.id || props.name}
        placeholder=" "
        maxLength={maxLength}
        onChange={handleChange}
        aria-invalid={Boolean(invalid)}
        aria-describedby={invalid ? `${props.name}-error` : undefined}
      />
      <label htmlFor={props.id || props.name}>
        {label}{props.required && <span className="ui-field__req"> *</span>}
      </label>
      <span className="ui-field__state" aria-hidden="true">
        <i className={`fas ${invalid ? 'fa-circle-exclamation' : 'fa-circle-check'}`} />
      </span>
      <div className="ui-field__foot">
        {invalid ? <span className="ui-field__error" id={`${props.name}-error`} role="alert">{meta.error}</span> : hint ? <span className="ui-field__hint">{hint}</span> : <span />}
        {maxLength && <span className="ui-field__count">{(field.value || '').length}/{maxLength}</span>}
      </div>
    </div>
  )
}

// Submit button that morphs between idle, loading, success and error states.
export const SubmitButton = ({ status = 'idle', disabled, labels = {}, className = '' }) => {
  const text = {
    idle: labels.idle || 'Submit',
    loading: labels.loading || 'Sending…',
    success: labels.success || 'Sent!',
    error: labels.error || 'Try again'
  }[status]
  const icon = { idle: 'fa-paper-plane', loading: 'fa-circle-notch fa-spin', success: 'fa-check', error: 'fa-rotate-right' }[status]

  return (
    <button type="submit" className={`ui-submit is-${status} ${className}`} disabled={disabled || status === 'loading'} data-magnetic>
      <span className="ui-submit__label">{text}</span>
      <i className={`fas ${icon}`} />
    </button>
  )
}

// Modal confirmation with an animated check / cross. Closes itself after `autoClose` ms.
export const ResultDialog = ({ open, type = 'success', title, message, onClose, autoClose = 3200 }) => {
  useEffect(() => {
    if (!open || !autoClose) return undefined
    const timer = setTimeout(onClose, autoClose)
    return () => clearTimeout(timer)
  }, [open, autoClose, onClose])

  useEffect(() => {
    if (!open) return undefined
    const onKey = (event) => { if (event.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null
  return (
    <div className="ui-dialog" onMouseDown={onClose}>
      <div className={`ui-dialog__card is-${type}`} role="alertdialog" aria-modal="true" aria-labelledby="ui-dialog-title" onMouseDown={(event) => event.stopPropagation()}>
        <svg className="ui-dialog__icon" viewBox="0 0 52 52" aria-hidden="true">
          <circle cx="26" cy="26" r="24" />
          {type === 'success' ? <path d="M15 27l7 7 15-16" /> : <path d="M18 18l16 16M34 18L18 34" />}
        </svg>
        <h3 id="ui-dialog-title">{title}</h3>
        <p>{message}</p>
        <button type="button" className="ui-dialog__close" onClick={onClose}>Close</button>
        {autoClose > 0 && <span className="ui-dialog__timer" style={{ animationDuration: `${autoClose}ms` }} />}
      </div>
    </div>
  )
}

export const BackLink = ({ onClick, children }) => (
  <button type="button" className="ui-back" onClick={onClick}>
    <i className="fas fa-arrow-left" /> {children}
  </button>
)
